<template>
  <div class="h-full">
    <n-layout class="app-shell h-screen flex flex-col" >
      <!-- Header -->
      <n-layout-header bordered class="grid grid-cols-2 px-7" v-if="!hidePanel">
        <div class="grid col-span-2">
          <div class="flex justify-between items-center gap-2">
            <div class="flex items-center gap-2">
            <Icon size="36">
              <UserAvatar />
            </Icon>
            <span>{{ auth.account?.username }}</span>
            </div>
            <span>
              <n-button v-if="auth.account" size="small" @click="router.push({ name: 'Logout' })">Logout</n-button>
              <n-button v-else size="small" @click="router.push({ name: 'Login' })">Login</n-button>
            </span>
          </div>
        </div>
        <div class="grid col-span-2 md:col-span-1">
          <div class="flex items-center gap-4 py-3">
            <img src="@/assets/images/logo.svg" alt="ARAG Logo" class="w-12" />
            <h1 class="text-lg md:text-xl lg:text-2xl font-bold">ARAG Forderungsmanagement</h1>
          </div>
        </div>
        <div class="grid col-span-2 md:col-span-1" >
          <div class="py-2 flex flex-wrap items-center gap-3 ml-auto">
            <n-button v-if="auth.hasAnyRole(['MEMBER', 'POLICYHOLDER'])" type="primary"
              class=" btn btn-arag-Rounded" @click="createClaim">
              Neue Forderung anlegen
            </n-button>
            <n-button type="secondary"  @click="router.push({ name: 'Notifications' })">
              <n-badge :value="notificationStore.unreadNotificationCount" :max="15">
                <span>
                  <AragIcon name="Mail" alt="Benachrichtigungen" />
                </span>
              </n-badge>
            </n-button>
          </div>
        </div>

      </n-layout-header>

      <!-- Main layout -->
      <n-layout has-sider class="flex-1 min-h-0">
        <!-- Sidebar -->
        <n-layout-sider
          v-if="!hidePanel"
          bordered
          collapse-mode="width"
          :collapsed-width="50"
          :width="200"
          v-model:collapsed="collapsed"
          :show-trigger="true"
          class="transition-all"
          :native-scrollbar="false"
        >
          <div tabindex="0">
            <n-menu
              :options="menuOptions"
              :collapsed="collapsed"
              :collapsed-width="50"
              :value="selectedKey"
              :theme-overrides="menuThemeOverrides"
              @update:value="handleMenuSelect"
            />
          </div>
        </n-layout-sider>

        <!-- Content -->
        <n-layout-content class="content-wrapper px-8 overflow-auto">
          <n-message-provider>
            <AragPageHeader class="mb-6" />
            <RouterView v-slot="{ Component }">
              <transition name="fade" mode="out-in">
                <component :is="Component" />
              </transition>
            </RouterView>
          </n-message-provider>
        </n-layout-content>
      </n-layout>

      <n-layout-footer
        v-if="showFooterActions"
        class="shrink-0 border-t !bg-white px-6 py-4 flex justify-end gap-3"
        style="border-color: var(--n-border-color)"
      >
        <n-button type="secondary" class="!mr-8 btn btn-arag-Rounded-Secondary" @click="onCancel"
          >Abbrechen</n-button
        >
        <n-button type="primary" class="!mr-8 btn btn-arag-Rounded" @click="onSave" :loading="footerActionsStore.saving"
          >{{ footerActionsStore.isCreateAction ? 'Speichern' : 'Aktualisieren' }}
        </n-button>
      </n-layout-footer>
    </n-layout>
  </div>
</template>

<script setup lang="ts">
import { type Component, computed, h, onMounted, ref } from 'vue'
import { RouterView, useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth.store.ts'
import { NIcon, type MenuOption, type MenuProps } from 'naive-ui'
import AragIcon from '@/components/AragIcon.vue'
import { Icon } from '@vicons/utils'
import {
  UserAvatar,
  UserProfile,
  Receipt,
  UserMultiple,
  Enterprise,
  User,
  Task,
  Settings,
  Email,
  Help,
} from '@vicons/carbon'
import AragPageHeader from '@/components/AragPageHeader.vue'
import { useFooterActionsStore } from '@/stores/footerActions.store.ts'
import { useNotificationStore } from '@/stores/notification.store.ts'
import type { RoleName } from '@/auth/roles.ts'
import { PAGE_ROLE_MAP } from '@/auth/page-role-map.ts'

const menuThemeOverrides: NonNullable<MenuProps['themeOverrides']> = {
  itemIconColorActive: '#000',
  itemIconColorActiveHover: '#000',
}

type AppMenuOption = MenuOption & {
  requiredRoles?: RoleName[]
}

const auth = useAuthStore()
auth.refreshRoles()
const collapsed = ref(false)

const notificationStore = useNotificationStore()

// Auto collapse sidebar on small screens
onMounted(() => {
  const handleResize = () => {
    collapsed.value = window.innerWidth < 768
  }
  handleResize()
  window.addEventListener('resize', handleResize)

  registerRoleRefreshOnFocus()
})

function renderIcon(icon: Component) {
  return () => h(NIcon, null, { default: () => h(icon) })
}

const allMenuOptions: AppMenuOption[] = [
  {
    icon: renderIcon(UserProfile),
    label: 'Meine Daten',
    key: 'baseData',
    requiredRoles: PAGE_ROLE_MAP.baseData,
  },
  {
    icon: renderIcon(Receipt),
    label: 'Forderungen',
    key: 'claims',
    requiredRoles: PAGE_ROLE_MAP.claims,
  },
  {
    icon: renderIcon(UserMultiple),
    label: 'Schuldner',
    key: 'debtors',
    requiredRoles: PAGE_ROLE_MAP.debtors,
  },
  {
    icon: renderIcon(Enterprise),
    label: 'Rechnungsstellen',
    key: 'creditors',
    requiredRoles: PAGE_ROLE_MAP.creditors,
  },
  { icon: renderIcon(User), label: 'Benutzer', key: 'users', requiredRoles: PAGE_ROLE_MAP.users },
  {
    icon: renderIcon(Task),
    label: 'Arbeitskorb',
    key: 'workbasket',
    requiredRoles: PAGE_ROLE_MAP.workBasket,
  },
  {
    icon: renderIcon(Email),
    label: 'Korrespondenz',
    key: 'correspondenz',
    requiredRoles: PAGE_ROLE_MAP.correspondenz
  },
  {
    icon: renderIcon(Settings),
    label: 'Administration',
    key: 'backofficeadmin',
    requiredRoles: PAGE_ROLE_MAP.backofficeAdmin,
  },
  { icon: renderIcon(Help), label: 'FAQ', key: 'faq', requiredRoles: PAGE_ROLE_MAP.faq },
]

const menuOptions = computed<MenuOption[]>(() =>
  allMenuOptions.filter((item) => {
    if (!item.requiredRoles || item.requiredRoles.length === 0) {
      return false
    }

    return auth.hasAnyRole(item.requiredRoles)
  }),
)

const footerActionsStore = useFooterActionsStore()

const router = useRouter()
const route = useRoute()

const showFooterActions = computed(() => route.meta.showFooterActions === true)
const hidePanel = computed(() => route.meta.hidePanel === true)

// one source of truth: menu key -> route location
const routeByKey = {
  home: { name: 'Home' },
  // dashboard: { name: 'Login' },
  claims: { name: 'Claims' },
  debtors: { name: 'Debtors' },
  creditors: { name: 'Creditors' },
  users: { name: 'Users' },
  baseData: { name: 'BaseData' },
  workbasket: { name: 'WorkBasket' },
  correspondenz: { name: 'Correspondenz' },
  backofficeadmin: { name: 'BackofficeAdmin' },
  faq: { name: 'FAQ' },
} as const

// derive selectedKey from current route name
const selectedKey = computed(() => {
  switch (route.name) {
    case 'Home':
      return 'home'
    case 'Claims':
      return 'claims'
    case 'Debtors':
      return 'debtors'
    case 'Users':
      return 'users'
    case 'Login':
      return 'claims'
    case 'Creditors':
      return 'creditors'
    case 'WorkBasket':
      return 'workbasket'
    case 'Correspondenz':
      return 'correspondenz'
    case 'BackofficeAdmin':
      return 'backofficeadmin'
    case 'BaseData':
      return 'baseData'
    case 'FAQ':
      return 'faq'
    default:
      return null
  }
})

function handleMenuSelect(key: string) {
  const target = (routeByKey as Record<string, { name: string }>)[key]
  if (target) {
    router.push(target)
  }
}

async function onSave() {
  await footerActionsStore.runSave()
}

async function onCancel() {
  await footerActionsStore.runCancel()
}

function createClaim() {
  router.push({ name: 'ClaimsNew' })
}

function registerRoleRefreshOnFocus() {
  const handler = async () => {
    if (document.visibilityState === 'visible') {
      try {
        await auth.refreshRoles()
      } catch (e) {
        console.error('Failed to refresh roles', e)
      }
    }
  }
  addEventListener('mousedown', handler)
}
</script>

<style>
.app-shell > .n-layout-scroll-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.app-shell .n-layout-sider-scroll-container {
  display: flex;
}

.app-shell > .n-layout-scroll-container > .n-layout {
  flex: 1;
  min-height: 0;
}

.app-shell .n-layout-content > .n-layout-scroll-container {
  padding-block: calc(var(--spacing) * 4);
}

.content-wrapper {
  background-color: #fafafa;
}

.readonly-input {
  background-color: #f5f5f5;
  cursor: not-allowed;
}
.n-menu-item-content__icon {
  color: blue;
}
</style>
